import React from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  ConnectionLineType,
  Controls,
  MiniMap,
  type Node,
  Panel,
  MarkerType,
  SelectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Workflow, ChevronRight } from 'lucide-react';
import { FOREST_GREEN, CHARCOAL, MID_GRAY } from '@/styles/tokens';
import { useEditorStore } from '@/stores/editor/editor-store';
import { nodeTypeConfig, nodeTypes, edgeTypes } from '@/components/workflow/WorkflowNodeTypes';
import type { WorkflowNodeData } from '@/types/workflow-editor';
import { useWorkflowEditor } from './WorkflowEditorContext.shared';
import { WorkflowSelector } from '@/components/editor/WorkflowSelector';
import { WorkflowNodePaletteSidebar } from './WorkflowNodePaletteSidebar';

const hasNodeTypeConfig = (type: string): type is keyof typeof nodeTypeConfig =>
  Object.prototype.hasOwnProperty.call(nodeTypeConfig, type);

const getMiniMapNodeColor = (node: Node<WorkflowNodeData>): string => {
  const config = hasNodeTypeConfig(node.data.type)
    ? nodeTypeConfig[node.data.type]
    : undefined;

  return config?.color?.replace('bg-', '#') || '#3b82f6';
};

const customStyles = `
  .react-flow__handle {
    width: 12px !important;
    height: 12px !important;
    border-radius: 50%;
    border: 2px solid white !important;
    transition: all 0.2s ease;
  }
  .react-flow__handle:hover {
    width: 16px !important;
    height: 16px !important;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
  }
  .react-flow__handle-left { left: -8px !important; }
  .react-flow__handle-right { right: -8px !important; }
  .react-flow__handle-top { top: -8px !important; }
  .react-flow__handle-bottom { bottom: -8px !important; }
  .react-flow__edge-path { stroke-width: 2px; }
  .react-flow__edge.selected .react-flow__edge-path { stroke: #f59e0b !important; stroke-width: 3px; }
  .react-flow__edge:hover .react-flow__edge-path { stroke: #60a5fa !important; stroke-width: 3px; }
  .react-flow__connection-path { stroke-width: 2px !important; }
`;

export const WorkflowCanvas: React.FC = () => {
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onReconnect,
    onNodeClick,
    onPaneClick,
    onEdgeClick,
    onEdgesDelete,
    onDrop,
    onDragOver,
    onInit,
    onSelectionChange,
    selectedNodes,
    reactFlowWrapper,
    embedded,
    workflow,
  } = useWorkflowEditor();

  const project = useEditorStore((state) => state.project);

  const canvasContent = (
    <TooltipProvider>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onReconnect={onReconnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onEdgeClick={onEdgeClick}
        onEdgesDelete={onEdgesDelete}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onInit={onInit}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        deleteKeyCode="Delete"
        edgesUpdatable={true}
        edgesFocusable={true}
        className={embedded ? 'bg-background' : 'bg-muted/20'}
        connectionLineStyle={{ stroke: '#3b82f6', strokeWidth: 2 }}
        connectionLineType={ConnectionLineType.SmoothStep}
        connectionRadius={40}
        snapToGrid={false}
        selectionOnDrag={true}
        selectionMode={SelectionMode.Partial}
        multiSelectionKeyCode={['Meta', 'Control']}
        panOnDrag={[1, 2]}
        onSelectionChange={onSelectionChange}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: true,
          style: { strokeWidth: 2, stroke: '#94a3b8' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' }
        }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls />
        <MiniMap
          nodeColor={getMiniMapNodeColor}
          maskColor="rgba(28, 31, 38, 0.7)"
          style={{ backgroundColor: 'hsl(224 13% 25%)' }}
          pannable
          zoomable
        />
        {/* Stats panel — only in standalone mode (embedded shows stats in header) */}
        {!embedded && (
          <Panel position="top-right" className="bg-card p-3 rounded-lg shadow-sm border space-y-2">
            <div className="text-xs text-muted-foreground">
              Nodes: {nodes.length} | Edges: {edges.length}
              {selectedNodes.length > 0 && (
                <span className="ml-2 text-blue-600">
                  | Selected: {selectedNodes.length}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-600 pt-2 border-t">
              <div className="font-semibold mb-1">Quick Guide:</div>
              <div className="space-y-1">
                <div>Drag nodes from left panel</div>
                <div>Connect: drag from green dot</div>
                <div>Delete: select + Delete key</div>
                <div>Edit: click node</div>
                <div>Multi-select: Cmd/Ctrl+click or drag</div>
                <div>Pan: middle/right mouse button</div>
              </div>
            </div>
          </Panel>
        )}
      </ReactFlow>
    </TooltipProvider>
  );

  // Standalone mode — no Card frame (standalone page has its own layout)
  if (!embedded) {
    return (
      <div className="h-full relative" ref={reactFlowWrapper}>
        <style>{customStyles}</style>
        {canvasContent}
      </div>
    );
  }

  // Embedded mode — Card frame matching ResponsiveEditorCanvas
  return (
    <div className="h-full relative p-4" ref={reactFlowWrapper}>
      <style>{customStyles}</style>
      <Card
        className={cn(
          "flex flex-col h-full overflow-hidden",
          "border border-border/50 bg-background/95 backdrop-blur-sm",
          "border-primary/30 shadow-md",
        )}
      >
        {/* Canvas Header — matches ResponsiveEditorCanvas header */}
        <div
          className="flex items-center justify-between px-4 py-2.5 border-b"
          style={{
            backgroundColor: `${FOREST_GREEN.DEFAULT}08`,
            borderColor: `${FOREST_GREEN.DEFAULT}30`,
          }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-1 h-5 rounded-full"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
            />
            <nav className="flex items-center gap-1" aria-label="Breadcrumb" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              {project && (
                <>
                  <span
                    className="text-sm font-medium tracking-tight"
                    style={{ color: CHARCOAL.DEFAULT }}
                  >
                    {project.name || 'Project'}
                  </span>
                  <ChevronRight
                    className="w-3.5 h-3.5 flex-shrink-0"
                    style={{ color: MID_GRAY.DEFAULT }}
                    aria-hidden="true"
                  />
                </>
              )}
              <span
                className="flex items-center gap-1.5 text-sm font-medium tracking-tight"
                style={{ color: FOREST_GREEN.DEFAULT }}
              >
                <Workflow className="w-3.5 h-3.5" aria-hidden="true" />
                {workflow?.name || 'Workflow'}
              </span>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-muted-foreground">
              Nodes: {nodes.length} | Edges: {edges.length}
              {selectedNodes.length > 0 && (
                <span className="ml-2 text-blue-600">| Selected: {selectedNodes.length}</span>
              )}
            </div>
            <WorkflowSelector />
          </div>
        </div>
        {/* ReactFlow + floating node palette */}
        <div className="flex-1 relative">
          {canvasContent}
          {/* Floating node palette inside canvas */}
          <div className="absolute top-2 left-2 z-10 w-64 max-h-[calc(100%-16px)] bg-card border border-border rounded-lg shadow-lg overflow-y-auto">
            <WorkflowNodePaletteSidebar />
          </div>
        </div>
      </Card>
    </div>
  );
};
